import React, { useState } from 'react';
import Navbar from '../../components/navigation/Navbar';
import {
  BarChart3,
  TrendingUp,
  Calendar,
  Download,
  AlertCircle,
  Users
} from 'lucide-react';

export default function Statistics() {
  const [dateRange, setDateRange] = useState('7days');

  const statCards = [
    {
      label: 'Total Detections',
      value: '12,543',
      change: '+12.5%',
      trend: 'up',
      icon: BarChart3,
      color: 'emerald'
    },
    {
      label: 'Unique Plates',
      value: '3,421',
      change: '+5.2%',
      trend: 'up',
      icon: Users,
      color: 'blue'
    },
    {
      label: 'Hot List Matches',
      value: '47',
      change: '+8.1%',
      trend: 'up',
      icon: AlertCircle,
      color: 'red'
    },
    {
      label: 'Avg Accuracy',
      value: '98.7%',
      change: '+0.3%',
      trend: 'up',
      icon: TrendingUp,
      color: 'purple'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      red: 'bg-red-50 text-red-600 border-red-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
    };
    return colors[color] || colors.emerald;
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">ALPR Statistics</h1>
              <p className="text-gray-600 mt-2">System performance and analytics overview</p>
            </div>
            <button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 px-4 rounded-lg transition flex items-center gap-2">
              <Download size={18} />
              Export Report
            </button>
          </div>

          {/* Date Range Selector */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold text-gray-900">Time Period:</span>
              <div className="flex gap-2">
                {[
                  { value: '24hours', label: 'Last 24h' },
                  { value: '7days', label: 'Last 7 days' },
                  { value: '30days', label: 'Last 30 days' },
                  { value: 'custom', label: 'Custom' }
                ].map((range) => (
                  <button
                    key={range.value}
                    onClick={() => setDateRange(range.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      dateRange === range.value
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statCards.map((card, idx) => {
              const Icon = card.icon;
              return (
                <div
                  key={idx}
                  className={`rounded-lg border p-6 transition hover:shadow-md ${getColorClasses(card.color)}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">{card.label}</p>
                      <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                    </div>
                    <Icon size={24} className="opacity-50" />
                  </div>
                  <p className={`text-sm font-semibold ${card.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {card.change} vs previous period
                  </p>
                </div>
              );
            })}
          </div>

          {/* Charts Placeholder */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Detections Over Time */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-6">Detections Over Time</h2>
              <div className="bg-gray-50 rounded-lg h-64 flex items-center justify-center">
                <p className="text-gray-400 text-sm">Chart placeholder</p>
              </div>
            </div>

            {/* Detection Accuracy */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-6">Detection Accuracy</h2>
              <div className="bg-gray-50 rounded-lg h-64 flex items-center justify-center">
                <p className="text-gray-400 text-sm">Chart placeholder</p>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Peak Hours */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Peak Hours</h3>
              <div className="space-y-3">
                {[
                  { time: '8:00 AM - 9:00 AM', count: 1234 },
                  { time: '12:00 PM - 1:00 PM', count: 1156 },
                  { time: '5:00 PM - 6:00 PM', count: 987 }
                ].map((hour, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{hour.time}</span>
                    <span className="font-semibold text-gray-900">{hour.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Locations */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Top Locations</h3>
              <div className="space-y-3">
                {[
                  { name: 'Downtown Zone A', count: 3421 },
                  { name: 'Highway 101', count: 2156 },
                  { name: 'Main Street', count: 1987 }
                ].map((loc, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{loc.name}</span>
                    <span className="font-semibold text-gray-900">{loc.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* System Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">System Status</h3>
              <div className="space-y-4">
                {[
                  { name: 'Cameras Online', value: '24 / 24', status: 'good' },
                  { name: 'Processing Queue', value: '0 items', status: 'good' },
                  { name: 'Database Health', value: '98.5%', status: 'good' }
                ].map((item, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-600">{item.name}</span>
                      <span className="text-sm font-semibold text-gray-900">{item.value}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full" style={{ width: '95%' }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}